import React, { useState, useEffect } from 'react';
import { Clock, Plus, Trash, AlertCircle, Info, ArrowUpCircle, ArrowDownCircle, Globe } from 'lucide-react';
import ApiService from '../../services/api';
import {
    convertScheduleToLocalTime,
    convertScheduleToUtcTime,
    getUserTimeZoneDisplay
} from '../common/timeZoneUtils';

const ScheduleEditor = ({ environmentName, initialSchedule, onSave, onCancel }) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [schedule, setSchedule] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isSuperUser, setIsSuperUser] = useState(false);
    const [userTimeZone, setUserTimeZone] = useState('');

    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

    // Check if user is a super user
    useEffect(() => {
        // This is a placeholder. In a real app, check the user's role from auth context
        const checkUserRole = async () => {
            try {
                setIsSuperUser(true);
            } catch (error) {
                console.error("Error checking user role:", error);
                setIsSuperUser(false);
            }
        };

        checkUserRole();
        setUserTimeZone(getUserTimeZoneDisplay());
    }, []);

    // Initialize schedule structure when component mounts or initialSchedule changes
    useEffect(() => {
        if (initialSchedule) {
            const localSchedule = convertScheduleToLocalTime(initialSchedule);

            const updatedSchedule = { ...localSchedule };
            days.forEach(day => {
                if (!updatedSchedule[day]) {
                    updatedSchedule[day] = [];
                }
            });

            setSchedule(updatedSchedule);
        } else {
            const emptySchedule = {};
            days.forEach(day => {
                emptySchedule[day] = [];
            });
            setSchedule(emptySchedule);
        }
    }, [initialSchedule]);

    const handleAddTimeSlot = (day) => {
        const newSchedule = { ...schedule };

        const existingSlots = newSchedule[day] || [];
        let defaultStart = "09:00";
        let defaultEnd = "17:00";

        if (existingSlots.length > 0) {
            const lastSlot = existingSlots[existingSlots.length - 1];
            defaultStart = lastSlot.end;

            const [hours, minutes] = lastSlot.end.split(":").map(Number);
            let endHours = hours + 8;
            if (endHours >= 24) {
                endHours = 23;
                defaultEnd = `${endHours}:59`;
            } else {
                defaultEnd = `${String(endHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
            }
        }

        newSchedule[day].push({
            start: defaultStart,
            end: defaultEnd,
            label: `Slot ${existingSlots.length + 1}`
        });

        setSchedule(newSchedule);
    };

    const handleRemoveTimeSlot = (day, index) => {
        const newSchedule = { ...schedule };
        newSchedule[day].splice(index, 1);
        setSchedule(newSchedule);
    };

    const handleTimeChange = (day, index, field, value) => {
        const newSchedule = { ...schedule };

        newSchedule[day][index] = {
            ...newSchedule[day][index],
            [field]: value
        };

        if (field === 'start' || field === 'end') {
            delete newSchedule[day][index].spansDays;
        }

        setSchedule(newSchedule);
    };

    const handleMoveTimeSlot = (day, index, direction) => {
        if ((direction === 'up' && index === 0) ||
            (direction === 'down' && index === schedule[day].length - 1)) {
            return;
        }

        const newSchedule = { ...schedule };
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        const temp = newSchedule[day][index];
        newSchedule[day][index] = newSchedule[day][targetIndex];
        newSchedule[day][targetIndex] = temp;

        setSchedule(newSchedule);
    };

    const validateSchedule = () => {
        let isValid = true;
        let errorMessage = "";

        days.forEach(day => {
            const slots = schedule[day] || [];

            const sortedSlots = [...slots].sort((a, b) => {
                return a.start.localeCompare(b.start);
            });

            for (let i = 0; i < sortedSlots.length - 1; i++) {
                const currentSlot = sortedSlots[i];
                const nextSlot = sortedSlots[i + 1];

                if (currentSlot.end > nextSlot.start) {
                    isValid = false;
                    errorMessage = `Overlapping time slots on ${day}`;
                    break;
                }
            }

            slots.forEach((slot, index) => {
                if (slot.start >= slot.end) {
                    isValid = false;
                    errorMessage = `End time must be after start time in slot ${index + 1} on ${day}`;
                }
            });
        });

        return { isValid, errorMessage };
    };

    const handleSave = async () => {
        const { isValid, errorMessage } = validateSchedule();

        if (!isValid) {
            setError(errorMessage);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const utcSchedule = convertScheduleToUtcTime(schedule);

            const scheduleData = {
                environmentId: environmentName,
                weeklySchedule: utcSchedule
            };

            if (onSave) {
                const success = await onSave(scheduleData);
                if (success) {
                    setIsDialogOpen(false);
                } else {
                    setError("Failed to save schedule: Server error");
                }
            } else {
                await ApiService.updateSchedule(environmentName, scheduleData);
                setIsDialogOpen(false);
            }
        } catch (error) {
            console.error('Failed to save schedule:', error);
            setError(error.message || 'Failed to save schedule');
        } finally {
            setIsLoading(false);
        }
    };

    const copyMondayToWeekdays = () => {
        if (!schedule.monday || schedule.monday.length === 0) {
            setError("Monday schedule is empty. Nothing to copy.");
            return;
        }

        const newSchedule = { ...schedule };
        ['tuesday', 'wednesday', 'thursday', 'friday'].forEach(day => {
            newSchedule[day] = [...schedule.monday];
        });

        setSchedule(newSchedule);
    };

    const clearWeekends = () => {
        const newSchedule = { ...schedule };
        newSchedule.saturday = [];
        newSchedule.sunday = [];
        setSchedule(newSchedule);
    };

    const scheduleButton = (
        <button
            onClick={() => setIsDialogOpen(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow-sm transition-colors"
            disabled={!isSuperUser}
        >
            <Clock className="w-4 h-4 mr-2" />
            {isSuperUser ? "Update Schedule" : "View Schedule"}
        </button>
    );

    if (!isDialogOpen) {
        return scheduleButton;
    }

    return (
        <>
            {scheduleButton}

            {/* Modal Dialog */}
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold">
                            {isSuperUser ? "Edit Schedule" : "View Schedule"} for {environmentName}
                        </h2>
                        <button
                            onClick={() => setIsDialogOpen(false)}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            ×
                        </button>
                    </div>

                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <Info className="h-5 w-5 text-blue-400" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-blue-700">
                                    You can add multiple time slots per day. The environment will start at the beginning of each time slot and stop at the end.
                                </p>
                                <div className="flex items-center mt-2 text-sm text-blue-700">
                                    <Globe className="h-4 w-4 mr-1" />
                                    <span>Times are displayed in your local time zone: <strong>{userTimeZone}</strong></span>
                                </div>
                                <p className="text-sm text-blue-700 mt-1">
                                    <em>Note: Times will be converted to UTC when saved. Schedules are executed in UTC time.</em>
                                </p>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <AlertCircle className="h-5 w-5 text-red-400" />
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-700">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {isSuperUser && (
                        <div className="flex mb-4 space-x-4">
                            <button
                                onClick={copyMondayToWeekdays}
                                className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded text-sm"
                            >
                                Copy Monday to Weekdays
                            </button>
                            <button
                                onClick={clearWeekends}
                                className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded text-sm"
                            >
                                Clear Weekends
                            </button>
                        </div>
                    )}

                    <div className="space-y-8">
                        {days.map(day => (
                            <div key={day} className="border-b pb-6">
                                <div className="flex justify-between items-center mb-3">
                                    <h3 className="text-md font-medium capitalize">{day}</h3>
                                    {isSuperUser && (
                                        <button
                                            onClick={() => handleAddTimeSlot(day)}
                                            className="inline-flex items-center px-2 py-1 text-sm bg-green-600 hover:bg-green-700 text-white rounded-md shadow-sm transition-colors"
                                        >
                                            <Plus className="w-3 h-3 mr-1" />
                                            Add Time Slot
                                        </button>
                                    )}
                                </div>

                                {schedule[day] && schedule[day].length > 0 ? (
                                    <div className="space-y-3">
                                        {schedule[day].map((slot, index) => (
                                            <div key={index} className={`flex items-center space-x-3 ${slot.isContinuation ? 'bg-gray-50 rounded p-2' : ''}`}>
                                                <div className="w-12 text-gray-500 text-sm">#{index + 1}</div>

                                                <input
                                                    type="time"
                                                    value={slot.start || '09:00'}
                                                    onChange={(e) => handleTimeChange(day, index, 'start', e.target.value)}
                                                    className="border rounded px-2 py-1"
                                                    disabled={!isSuperUser || slot.isContinuation}
                                                />

                                                <span className="text-gray-500">to</span>

                                                <input
                                                    type="time"
                                                    value={slot.end || '17:00'}
                                                    onChange={(e) => handleTimeChange(day, index, 'end', e.target.value)}
                                                    className="border rounded px-2 py-1"
                                                    disabled={!isSuperUser}
                                                />

                                                <input
                                                    type="text"
                                                    placeholder="Label (optional)"
                                                    value={slot.label || ''}
                                                    onChange={(e) => handleTimeChange(day, index, 'label', e.target.value)}
                                                    className="border rounded px-2 py-1 flex-1"
                                                    disabled={!isSuperUser || slot.isContinuation}
                                                />

                                                {slot.spansDays && (
                                                    <div className="text-xs text-blue-600 whitespace-nowrap">
                                                        Spans to next day
                                                    </div>
                                                )}

                                                {slot.isContinuation && (
                                                    <div className="text-xs text-gray-600 whitespace-nowrap">
                                                        Continued from previous day
                                                    </div>
                                                )}

                                                {isSuperUser && !slot.isContinuation && (
                                                    <>
                                                        <button
                                                            onClick={() => handleMoveTimeSlot(day, index, 'up')}
                                                            className="text-gray-500 hover:text-gray-700"
                                                            disabled={index === 0}
                                                        >
                                                            <ArrowUpCircle className={`w-4 h-4 ${index === 0 ? 'opacity-30' : ''}`} />
                                                        </button>

                                                        <button
                                                            onClick={() => handleMoveTimeSlot(day, index, 'down')}
                                                            className="text-gray-500 hover:text-gray-700"
                                                            disabled={index === schedule[day].length - 1}
                                                        >
                                                            <ArrowDownCircle className={`w-4 h-4 ${index === schedule[day].length - 1 ? 'opacity-30' : ''}`} />
                                                        </button>

                                                        <button
                                                            onClick={() => handleRemoveTimeSlot(day, index)}
                                                            className="text-red-500 hover:text-red-700"
                                                        >
                                                            <Trash className="w-4 h-4" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-gray-500 italic text-sm">No scheduled activity for {day}.</div>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="flex justify-end space-x-2 mt-6">
                        <button
                            onClick={() => {
                                setIsDialogOpen(false);
                                if (onCancel) onCancel();
                            }}
                            className="px-4 py-2 text-gray-600 hover:text-gray-800"
                        >
                            Cancel
                        </button>

                        {isSuperUser && (
                            <button
                                onClick={handleSave}
                                disabled={isLoading}
                                className={`px-4 py-2 text-white rounded-md ${
                                    isLoading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
                                }`}
                            >
                                {isLoading ? 'Saving...' : 'Save Changes'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ScheduleEditor;